/**
 * DataTable — a sortable list with attached row actions.
 *
 * Used by:
 *  - The complex-array editor (each row = a zoomable record).
 *  - The scalar-array editor (each row = an inline value editor).
 *  - Anywhere else a "sort + delete + click" list pattern shows up.
 *
 * What this component owns:
 *  - Drag-to-reorder via HTML5 drag-and-drop. The drag handle is
 *    implicit on the leading index column; the user grabs that and
 *    drops onto another row.
 *  - The visually-attached delete button on the right (matches the
 *    "recent projects" row treatment — a button that shares the
 *    row's surface, separated by a vertical divider).
 *  - The row's primary content slot — caller decides what's inside
 *    (a summary line, a chip editor, a oneOf inline picker, …).
 *
 * What this component does NOT own:
 *  - The data store. Reorder/delete are emitted as callbacks; the
 *    parent decides whether they go through the store, the schema
 *    engine, or anywhere else.
 *  - Click-to-zoom semantics. Pass a `onClick` to make rows clickable.
 *
 * Keyboard support: row tabbable, Enter/Space activates onClick (if
 * any), Delete triggers onDelete. The drag handle is mouse-only —
 * accessible reorder keeps the existing ▲/▼ buttons live in the
 * complex-array list (where they're already tested).
 */

import { type CSSProperties, type ReactNode, useState } from 'react';

import { Icon } from './Icon.js';

import './DataTable.css';

export interface DataTableRow {
  /** Stable id per row — used as the React key and for identifying
   * drag drops. For arrays this is typically the index as a string. */
  id: string;
  /**
   * Primary content shown in the row body. Caller decides what it is
   * — summary text, an inline editor, a chip. Keep it single-line
   * where possible; the row is sized for one line of content.
   */
  body: ReactNode;
  /** Optional leading label — typically a number ("1", "2", …) or
   * a name. Sits in the drag-handle column on the left. */
  leading?: ReactNode;
  /** Click anywhere on the row body, like a record-row that zooms in.
   * Optional — omit for fully-inline rows like scalar arrays. */
  onClick?: () => void;
}

interface Props {
  rows: readonly DataTableRow[];
  /**
   * Reorder handler. Called with `(fromIndex, toIndex)` after a
   * successful drop. `to` is the index the row should be inserted
   * at — same convention as `Array.splice`. Omit to disable
   * reordering entirely (the drag handle won't render).
   */
  onReorder?: (from: number, to: number) => void;
  /**
   * Delete handler for a single row. Optional — omit to hide the
   * delete column (e.g. for read-only tables).
   */
  onDelete?: (index: number) => void;
  /** ARIA label describing what these rows are. */
  ariaLabel?: string;
}

export function DataTable({ rows, onReorder, onDelete, ariaLabel }: Props): JSX.Element {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  const reorderable = onReorder !== undefined;

  const handleDragStart =
    (index: number) =>
    (e: React.DragEvent<HTMLLIElement>): void => {
      if (!reorderable) return;
      setDragIndex(index);
      // Some browsers refuse to start a drag without dataTransfer payload.
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(index));
    };

  const handleDragOver =
    (index: number) =>
    (e: React.DragEvent<HTMLLIElement>): void => {
      if (!reorderable || dragIndex === null) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (dropIndex !== index) setDropIndex(index);
    };

  const handleDragEnd = (): void => {
    setDragIndex(null);
    setDropIndex(null);
  };

  const handleDrop =
    (index: number) =>
    (e: React.DragEvent<HTMLLIElement>): void => {
      if (!reorderable || dragIndex === null) return;
      e.preventDefault();
      if (dragIndex !== index) onReorder?.(dragIndex, index);
      setDragIndex(null);
      setDropIndex(null);
    };

  return (
    <ul className="data-table" aria-label={ariaLabel}>
      {rows.map((row, i) => {
        const isDragging = dragIndex === i;
        const isDropTarget = dropIndex === i && dragIndex !== null && dragIndex !== i;
        const className = [
          'data-table-row',
          isDragging && 'data-table-row-dragging',
          isDropTarget && 'data-table-row-drop-target',
          row.onClick && 'data-table-row-clickable',
        ]
          .filter(Boolean)
          .join(' ');
        const style: CSSProperties = {};
        return (
          // The li IS interactive in this context — it's a draggable
          // element whose drag events drive the reorder. Drag handlers
          // on a non-button element are the standard HTML5 DnD pattern;
          // there's no semantic role that matches better. We provide
          // accessible reorder via the existing ▲/▼ buttons in the
          // complex-array list separately.
          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
          <li
            key={row.id}
            className={className}
            style={style}
            draggable={reorderable}
            onDragStart={handleDragStart(i)}
            onDragOver={handleDragOver(i)}
            onDragEnd={handleDragEnd}
            onDrop={handleDrop(i)}
          >
            {reorderable && (
              <span className="data-table-handle" aria-hidden="true" title="Drag to reorder">
                <Icon name="drag-handle" size={14} />
              </span>
            )}
            {row.leading !== undefined && <span className="data-table-leading">{row.leading}</span>}
            <div
              className="data-table-body"
              {...(row.onClick && {
                role: 'button',
                tabIndex: 0,
                onClick: row.onClick,
                onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    row.onClick?.();
                  }
                },
              })}
            >
              {row.body}
            </div>
            {onDelete && (
              <button
                type="button"
                className="data-table-delete"
                aria-label={`Delete row ${i + 1}`}
                onClick={(e) => {
                  // Stop propagation so the row's onClick doesn't fire
                  // when the user is just trying to delete.
                  e.stopPropagation();
                  onDelete(i);
                }}
              >
                ×
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
